<script lang="ts" setup>
  import type Stepper from 'primevue/stepper'
  import type { GiftCardOrderResponse, GiftCardSkin } from '~/types/api/gift-cards'
  import type { EmitNextStepPayload } from '../types/wizard'

  const giftCardStore = useGiftCardsStore()

  const stepperRef = ref<InstanceType<typeof Stepper> | null>(null)

  onBeforeMount(() => {
    // If the active step from the store is the purchase step (4) when the component is mounted,
    // go back to the personalise step (2).
    if (giftCardStore.wizard.activeStep === 4) {
      giftCardStore.wizard.activeStep = 2
    }
  })

  function scrollToTop() {
    const scrollToPosition = stepperRef.value ? (stepperRef.value as any).$el.getBoundingClientRect().top : 0

    setTimeout(() => {
      if (scrollToPosition <= 0) {
        window.scrollTo({ top: scrollToPosition, behavior: 'smooth' })
      }
    }, 100)
  }

  function handleNextStep(nextStep: () => void) {
    scrollToTop()
    nextStep()
  }

  function onDesignSelect(design: GiftCardSkin, nextStep: () => void) {
    giftCardStore.wizard.values.skin = design

    handleNextStep(nextStep)
  }

  const paymentResponse = ref<GiftCardOrderResponse | null>(null)

  function handlePersonaliseNextStep(payload: EmitNextStepPayload, nextStep: (step: number) => void) {
    if (payload.step === 'payment') {
      paymentResponse.value = payload.paymentDetails
    }

    handleNextStep(() => nextStep(payload.step === 'payment' ? 4 : 3))
  }

  function handleRecipientsNextStep(payload: EmitNextStepPayload, nextStep: (step: number) => void) {
    if (payload.step === 'payment') {
      paymentResponse.value = payload.paymentDetails
    }

    handleNextStep(() => nextStep(4))
  }
</script>

<template>
  <Stepper ref="stepperRef" v-model:value="giftCardStore.wizard.activeStep" class="c-gift-card-stepper">
    <CGiftCardStepList />

    <StepPanels
      :pt="{
        root: {
          class: 'mb-16'
        }
      }"
    >
      <StepPanel v-slot="{ activateCallback }" :value="1">
        <CGiftCardDesign @design-selected="onDesignSelect($event, () => activateCallback(2))" />
      </StepPanel>

      <StepPanel v-slot="{ activateCallback }" :value="2">
        <CGiftCardPersonalise
          @next-step="handlePersonaliseNextStep($event, (step: number) => activateCallback(step))"
        />
      </StepPanel>

      <StepPanel v-slot="{ activateCallback }" :value="3">
        <CGiftCardRecipients @next-step="handleRecipientsNextStep($event, (step: number) => activateCallback(step))" />
      </StepPanel>

      <StepPanel v-slot="{ active }" :value="4">
        <CGiftCardPurchase :active :payment-response="paymentResponse" />
      </StepPanel>
    </StepPanels>
  </Stepper>
</template>
